import React, { useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  AUTOMATIC_FIELD_NOTE,
  AuditOperation,
  AuditValueSide,
  CHANGES_COLUMN_LABELS,
  EMPTY_FIELD_LIST,
} from '../../EntityAuditHistory.constants';
import {
  AuditFieldListPropType,
  AuditFieldRenderersPropType,
  AuditRenderersPropType,
} from '../../EntityAuditHistory.propTypes';
import {
  becameValueStyles,
  changesTableStyles,
  diffArrowStyles,
  diffHeaderStyles,
  fieldLabelStyles,
  getDiffBodyRowStyles,
  getInlineChangeRowStyles,
  getNewValueStyles,
  getOldValueStyles,
  inlineChangesStyles,
  inlineFieldLabelStyles,
  inlineValueStyles,
  summaryValueStyles,
} from '../../EntityAuditHistory.styles';
import { EntityAuditHistoryTestIds } from '../../EntityAuditHistory.testIds';
import {
  formatAuditValue,
  getFieldRenderer,
  getSummaryRenderer,
  sortAutomaticFieldsLast,
  withChangeRowKeys,
} from '../../EntityAuditHistory.utils';
import AuditFieldValue from '../AuditFieldValue';
import AuditSummaryValue from '../AuditSummaryValue';

/**
 * The per-field diff: Field / Was / Became, or Field / Value for a single-sided Create or Delete. A
 * change carrying a server `summary` renders through `renderers[summary.kind]` across both value
 * columns, falling back to "Field replaced" — the raw value is never shown for it. Any other change
 * whose field has a `fieldRenderers` entry renders each value through it instead of the raw value.
 * Inline, each field is one row with its old value struck before the new one, and there is no header.
 * Automatic fields go last, muted.
 */
const AuditChangesTable = ({
  changes,
  keyPrefix,
  operation = null,
  columnLabels = null,
  renderers = null,
  fieldRenderers = null,
  row = null,
  detail = null,
  automaticFields = EMPTY_FIELD_LIST,
  isInline = false,
}) => {
  const keyedChanges = useMemo(
    () => withChangeRowKeys(keyPrefix, sortAutomaticFieldsLast(changes, automaticFields)),
    [keyPrefix, changes, automaticFields],
  );
  const labels = useMemo(() => ({ ...CHANGES_COLUMN_LABELS, ...columnLabels }), [columnLabels]);

  const isCreate = operation === AuditOperation.CREATED;
  const isSingleSided = isCreate || operation === AuditOperation.DELETED;
  const valueVariant = isInline ? 'regular12' : 'regular14';
  const newValueVariant = isInline ? 'medium12' : 'medium14';

  return (
    <Box
      data-testid={EntityAuditHistoryTestIds.CHANGES_TABLE}
      sx={isInline ? inlineChangesStyles : changesTableStyles}
    >
      {!isInline && (
        <Box sx={diffHeaderStyles}>
          <Typography color="text.secondary" variant="medium12">
            {labels.field}
          </Typography>
          <Typography color="text.secondary" variant="medium12">
            {isSingleSided ? labels.value : labels.was}
          </Typography>
          {!isSingleSided && (
            <Typography color="text.secondary" variant="medium12">
              {labels.became}
            </Typography>
          )}
        </Box>
      )}

      {keyedChanges.map((change, index) => {
        const hasSummary = !_.isNil(change.summary);
        const isAutomatic = automaticFields.includes(change.field);
        const FieldRenderer = getFieldRenderer(fieldRenderers, change.field);
        const hasFieldRenderer = !_.isNil(FieldRenderer);
        // A field renderer may draw block content (e.g. the metadata settings list), which can't
        // sit inside a span.
        const valueComponent = hasFieldRenderer ? 'div' : 'span';

        const renderValue = (value, side) =>
          hasFieldRenderer ? (
            <AuditFieldValue
              change={change}
              detail={detail}
              renderer={FieldRenderer}
              row={row}
              side={side}
              value={value}
            />
          ) : (
            formatAuditValue(value)
          );

        const values = hasSummary ? (
          <Box data-testid={EntityAuditHistoryTestIds.CHANGE_SUMMARY} sx={summaryValueStyles}>
            <AuditSummaryValue
              change={change}
              detail={detail}
              renderer={getSummaryRenderer(renderers, change.summary)}
              row={row}
            />
          </Box>
        ) : (
          <>
            <Typography
              component={valueComponent}
              data-testid={EntityAuditHistoryTestIds.CHANGE_OLD_VALUE}
              sx={getOldValueStyles({ isSingleSided, hasFieldRenderer })}
              variant={valueVariant}
            >
              {renderValue(
                isCreate ? change.new : change.old,
                isSingleSided ? AuditValueSide.SINGLE : AuditValueSide.OLD,
              )}
            </Typography>

            {!isSingleSided && (
              <Box component={valueComponent} sx={becameValueStyles}>
                <Box component="span" sx={diffArrowStyles}>
                  →
                </Box>
                <Typography
                  color={isAutomatic ? 'text.secondary' : 'text.primary'}
                  component={valueComponent}
                  data-testid={EntityAuditHistoryTestIds.CHANGE_NEW_VALUE}
                  sx={getNewValueStyles(hasFieldRenderer)}
                  variant={newValueVariant}
                >
                  {renderValue(change.new, AuditValueSide.NEW)}
                </Typography>
              </Box>
            )}
          </>
        );

        return (
          <Box
            key={change.key}
            sx={
              isInline
                ? getInlineChangeRowStyles(isAutomatic)
                : getDiffBodyRowStyles(index % 2 === 1)
            }
          >
            <Box sx={isInline ? inlineFieldLabelStyles : fieldLabelStyles}>
              <Typography
                color={isAutomatic ? 'text.secondary' : 'text.primary'}
                data-testid={EntityAuditHistoryTestIds.CHANGE_FIELD}
                variant={isAutomatic ? 'regular14' : 'medium14'}
              >
                {change.field}
              </Typography>
              {isAutomatic && (
                <Typography
                  color="text.secondary"
                  data-testid={EntityAuditHistoryTestIds.CHANGE_AUTOMATIC_NOTE}
                  variant="regular12"
                >
                  {AUTOMATIC_FIELD_NOTE}
                </Typography>
              )}
            </Box>

            {isInline ? <Box sx={inlineValueStyles}>{values}</Box> : values}
          </Box>
        );
      })}
    </Box>
  );
};

AuditChangesTable.propTypes = {
  changes: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      summary: PropTypes.shape({ kind: PropTypes.string.isRequired }),
    }),
  ).isRequired,
  keyPrefix: PropTypes.string.isRequired,
  operation: PropTypes.string,
  columnLabels: PropTypes.shape({
    field: PropTypes.string,
    was: PropTypes.string,
    became: PropTypes.string,
    value: PropTypes.string,
  }),
  renderers: AuditRenderersPropType,
  fieldRenderers: AuditFieldRenderersPropType,
  row: PropTypes.object,
  detail: PropTypes.object,
  automaticFields: AuditFieldListPropType,
  isInline: PropTypes.bool,
};

export default AuditChangesTable;
